import { useEffect, useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { KeyRound, LogOut, ShieldOff, User as UserIcon } from 'lucide-react';
import { toast } from 'sonner';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import {
  Card,
  CardContent,
  CardDescription,
  CardHeader,
  CardTitle,
} from '@/components/ui/card';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Skeleton } from '@/components/ui/skeleton';
import { api, extractErrorMessage } from '@/lib/api-client';
import { useAuthStore, type UserRole } from '@/features/auth/store';
import { formatDateTime } from '@/lib/format';

interface AuthMeResponse {
  data: {
    id: string;
    tenantId: string;
    name: string;
    email: string;
    role: UserRole;
    active: boolean;
    phone: string | null;
    createdAt: string;
    lastLoginAt: string | null;
    tenant: {
      id: string;
      name: string;
      cnpj: string;
      status: string;
      plan: { code: string; name: string };
    };
  };
}

const ROLE_LABEL: Record<UserRole, string> = {
  admin: 'Administrador',
  manager: 'Gestor',
  driver: 'Motorista',
  workshop: 'Oficina',
};

function useAuthMe() {
  return useQuery({
    queryKey: ['auth', 'me'],
    queryFn: async () => {
      const { data } = await api.get<AuthMeResponse>('/auth/me');
      return data.data;
    },
  });
}

export function ProfilePage() {
  const meQuery = useAuthMe();
  const queryClient = useQueryClient();
  const setUserInStore = useAuthStore((s) => s.user);
  // setSession existe mas só atualiza tudo de uma vez; vamos refletir o nome
  // via invalidação do auth.me + recarregar quando precisar.

  // Estado do formulário de dados pessoais
  const [name, setName] = useState('');
  const [phone, setPhone] = useState('');

  useEffect(() => {
    if (meQuery.data) {
      setName(meQuery.data.name);
      setPhone(meQuery.data.phone ?? '');
    }
  }, [meQuery.data]);

  const updateProfile = useMutation({
    mutationFn: async (payload: { name: string; phone: string }) => {
      const { data } = await api.patch<AuthMeResponse>('/auth/me', {
        name: payload.name,
        phone: payload.phone,
      });
      return data.data;
    },
    onSuccess: () => {
      toast.success('Perfil atualizado');
      queryClient.invalidateQueries({ queryKey: ['auth', 'me'] });
      queryClient.invalidateQueries({ queryKey: ['tenant'] });
    },
    onError: (err) => toast.error(extractErrorMessage(err)),
  });

  const dirty =
    meQuery.data != null &&
    (name.trim() !== meQuery.data.name || (phone.trim() || null) !== meQuery.data.phone);

  // Estado do formulário de senha
  const [currentPassword, setCurrentPassword] = useState('');
  const [newPassword, setNewPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');

  const changePassword = useMutation({
    mutationFn: async (payload: { currentPassword: string; newPassword: string }) => {
      await api.post('/auth/change-password', payload);
    },
    onSuccess: () => {
      toast.success('Senha alterada com sucesso');
      setCurrentPassword('');
      setNewPassword('');
      setConfirmPassword('');
    },
    onError: (err) => toast.error(extractErrorMessage(err)),
  });

  const passwordMismatch =
    confirmPassword.length > 0 && newPassword !== confirmPassword;
  const passwordTooShort = newPassword.length > 0 && newPassword.length < 10;
  const canSubmitPassword =
    currentPassword.length > 0 &&
    newPassword.length >= 10 &&
    confirmPassword === newPassword &&
    !changePassword.isPending;

  // Encerrar sessões
  const [confirmLogoutAll, setConfirmLogoutAll] = useState(false);
  const logoutAll = useMutation({
    mutationFn: async () => {
      const { data } = await api.post<{ data: { revokedCount: number } }>('/auth/logout-all');
      return data.data;
    },
    onSuccess: (result) => {
      toast.success(
        `${result.revokedCount} sessã${result.revokedCount === 1 ? 'o' : 'ões'} encerrada${result.revokedCount === 1 ? '' : 's'}`,
      );
      setConfirmLogoutAll(false);
    },
    onError: (err) => toast.error(extractErrorMessage(err)),
  });

  const me = meQuery.data;

  return (
    <div className="page">
      <header className="space-y-1.5">
        <p className="text-sm text-muted-foreground">Sua conta</p>
        <h1 className="text-3xl font-semibold tracking-tight">Meu perfil</h1>
        <p className="text-sm text-muted-foreground">
          Gerencie seus dados, senha e sessões ativas.
        </p>
      </header>

      <div className="grid gap-6 lg:grid-cols-3">
        {/* Coluna esquerda: cards */}
        <div className="space-y-6 lg:col-span-2">
          {/* Dados pessoais */}
          <Card>
            <CardHeader>
              <CardTitle className="flex items-center gap-2 text-base">
                <UserIcon className="size-4" />
                Dados pessoais
              </CardTitle>
              <CardDescription>
                Email e papel não podem ser alterados aqui. Para mudá-los, peça a um administrador.
              </CardDescription>
            </CardHeader>
            <CardContent className="space-y-4">
              {meQuery.isLoading ? (
                <div className="space-y-3">
                  <Skeleton className="h-10 w-full" />
                  <Skeleton className="h-10 w-full" />
                </div>
              ) : (
                <>
                  <div className="grid gap-4 sm:grid-cols-2">
                    <div className="space-y-1.5">
                      <Label htmlFor="name">Nome completo</Label>
                      <Input
                        id="name"
                        value={name}
                        onChange={(e) => setName(e.target.value)}
                        maxLength={120}
                        autoComplete="name"
                      />
                    </div>
                    <div className="space-y-1.5">
                      <Label htmlFor="phone">Telefone</Label>
                      <Input
                        id="phone"
                        value={phone}
                        onChange={(e) => setPhone(e.target.value)}
                        placeholder="+5511988887777"
                        maxLength={20}
                        autoComplete="tel"
                      />
                    </div>
                  </div>

                  <div className="grid gap-4 sm:grid-cols-2">
                    <div className="space-y-1.5">
                      <Label className="text-muted-foreground">Email</Label>
                      <Input value={me?.email ?? ''} disabled />
                    </div>
                    <div className="space-y-1.5">
                      <Label className="text-muted-foreground">Papel</Label>
                      <Input value={me ? ROLE_LABEL[me.role] : ''} disabled />
                    </div>
                  </div>

                  <div className="flex justify-end">
                    <Button
                      onClick={() => updateProfile.mutate({ name: name.trim(), phone: phone.trim() })}
                      disabled={!dirty || updateProfile.isPending || name.trim().length === 0}
                    >
                      {updateProfile.isPending ? 'Salvando…' : 'Salvar alterações'}
                    </Button>
                  </div>
                </>
              )}
            </CardContent>
          </Card>

          {/* Alterar senha */}
          <Card>
            <CardHeader>
              <CardTitle className="flex items-center gap-2 text-base">
                <KeyRound className="size-4" />
                Alterar senha
              </CardTitle>
              <CardDescription>
                Sua nova senha precisa ter no mínimo 10 caracteres.
              </CardDescription>
            </CardHeader>
            <CardContent className="space-y-4">
              <div className="space-y-1.5">
                <Label htmlFor="current-password">Senha atual</Label>
                <Input
                  id="current-password"
                  type="password"
                  value={currentPassword}
                  onChange={(e) => setCurrentPassword(e.target.value)}
                  autoComplete="current-password"
                />
              </div>
              <div className="grid gap-4 sm:grid-cols-2">
                <div className="space-y-1.5">
                  <Label htmlFor="new-password">Nova senha</Label>
                  <Input
                    id="new-password"
                    type="password"
                    value={newPassword}
                    onChange={(e) => setNewPassword(e.target.value)}
                    autoComplete="new-password"
                  />
                  {passwordTooShort && (
                    <p className="text-xs text-destructive">
                      Mínimo de 10 caracteres.
                    </p>
                  )}
                </div>
                <div className="space-y-1.5">
                  <Label htmlFor="confirm-password">Confirmar nova senha</Label>
                  <Input
                    id="confirm-password"
                    type="password"
                    value={confirmPassword}
                    onChange={(e) => setConfirmPassword(e.target.value)}
                    autoComplete="new-password"
                  />
                  {passwordMismatch && (
                    <p className="text-xs text-destructive">As senhas não coincidem.</p>
                  )}
                </div>
              </div>
              <div className="flex justify-end">
                <Button
                  onClick={() =>
                    changePassword.mutate({ currentPassword, newPassword })
                  }
                  disabled={!canSubmitPassword}
                >
                  {changePassword.isPending ? 'Alterando…' : 'Alterar senha'}
                </Button>
              </div>
            </CardContent>
          </Card>

          {/* Sessões */}
          <Card>
            <CardHeader>
              <CardTitle className="flex items-center gap-2 text-base">
                <ShieldOff className="size-4" />
                Sessões ativas
              </CardTitle>
              <CardDescription>
                Encerre o acesso em todos os outros dispositivos onde você esteja logado.
                Você continuará logado neste dispositivo.
              </CardDescription>
            </CardHeader>
            <CardContent className="flex items-center justify-between gap-4">
              <p className="text-sm text-muted-foreground">
                Útil caso suspeite que sua conta foi acessada por outra pessoa.
              </p>
              <Button
                variant="destructive"
                onClick={() => setConfirmLogoutAll(true)}
                disabled={logoutAll.isPending}
              >
                <LogOut className="size-4" />
                Encerrar todas as sessões
              </Button>
            </CardContent>
          </Card>
        </div>

        {/* Coluna direita: resumo da conta */}
        <div className="space-y-6">
          <Card>
            <CardHeader>
              <CardTitle className="text-base">Resumo</CardTitle>
            </CardHeader>
            <CardContent className="space-y-3 text-sm">
              {meQuery.isLoading || !me ? (
                <>
                  <Skeleton className="h-4 w-3/4" />
                  <Skeleton className="h-4 w-1/2" />
                  <Skeleton className="h-4 w-2/3" />
                </>
              ) : (
                <>
                  <div>
                    <p className="text-xs text-muted-foreground">Empresa</p>
                    <p className="font-medium">{me.tenant.name}</p>
                    <p className="font-mono text-xs text-muted-foreground">
                      CNPJ {me.tenant.cnpj}
                    </p>
                  </div>
                  <div className="flex gap-2">
                    <Badge variant="outline">Plano {me.tenant.plan.name}</Badge>
                    {me.tenant.status === 'trial' && (
                      <Badge variant="warning">Trial</Badge>
                    )}
                    {me.active ? (
                      <Badge variant="success">Ativo</Badge>
                    ) : (
                      <Badge variant="destructive">Inativo</Badge>
                    )}
                  </div>
                  <div className="pt-2">
                    <p className="text-xs text-muted-foreground">Conta criada em</p>
                    <p className="text-sm">{formatDateTime(me.createdAt)}</p>
                  </div>
                  <div>
                    <p className="text-xs text-muted-foreground">Último login</p>
                    <p className="text-sm">{formatDateTime(me.lastLoginAt)}</p>
                  </div>
                </>
              )}
            </CardContent>
          </Card>

          {setUserInStore?.id && (
            <p className="px-2 text-[11px] text-muted-foreground">
              ID #{setUserInStore.id.slice(0, 8)}…
            </p>
          )}
        </div>
      </div>

      {/* Dialog de confirmação */}
      <Dialog open={confirmLogoutAll} onOpenChange={setConfirmLogoutAll}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Encerrar todas as sessões?</DialogTitle>
            <DialogDescription>
              Você será desconectado em todos os outros dispositivos. Esta sessão atual
              continuará ativa.
            </DialogDescription>
          </DialogHeader>
          <DialogFooter>
            <Button variant="ghost" onClick={() => setConfirmLogoutAll(false)}>
              Cancelar
            </Button>
            <Button
              variant="destructive"
              onClick={() => logoutAll.mutate()}
              disabled={logoutAll.isPending}
            >
              {logoutAll.isPending ? 'Encerrando…' : 'Confirmar'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
